/**
 * EmployeeDocumentsTab — Carpeta digital del empleado.
 *
 * Muestra árbol de carpetas (izquierda) + lista de documentos (derecha).
 * Permite subir documentos, crear carpetas, archivar y descargar.
 */
import { useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import { DataTable, type DataTableColumn } from '@/components/ds/DataTable'
import {
  FolderOpen,
  Folder,
  Upload,
  FolderPlus,
  Download,
  Archive,
  Trash2,
  Eye,
  EyeOff,
  FileText,
  Search,
  ChevronRight,
  Move,
  FileBadge,
} from 'lucide-react'
import {
  useEmployeeFolders,
  useEmployeeDocuments,
  useUploadDocument,
  useEmitirCertificadoAntiguedad,
  useUpdateDocument,
  useDeleteDocument,
  useCreateEmployeeFolder,
  useDocumentTypes,
  formatFileSize,
  type EmployeeFolder,
  type EmployeeDocument,
} from '../hooks/useHrDocuments'
import { useContracts } from '../hooks/usePayroll'
import { useToast } from '@/components/Toast'
import { documentoGuardado, useVisorPdf } from '@/components/VisorPdf'
import { TableSkeleton } from '@/components/Skeleton'
import { EmptyState } from '@/components/EmptyState'
import { extractErrorMessage } from '@/lib/errorUtils'
import apiClient from '@/lib/apiClient'
import { Modal } from '@/components/ds/Modal'

interface Props {
  employeeId: string
}

// ─── Upload Modal ─────────────────────────────────────────────────────────────

interface UploadModalProps {
  employeeId: string
  folderId: string | null
  onClose: () => void
}

function UploadModal({ employeeId, folderId, onClose }: UploadModalProps) {
  const { data: docTypes } = useDocumentTypes()
  const { data: contractsData } = useContracts({ employee_id: employeeId, page_size: 99 })
  const contracts = (contractsData?.contracts ?? contractsData?.results ?? []) as Array<{ id: string; contract_type: string; start_date: string; end_date: string | null }>
  const uploadMutation = useUploadDocument(employeeId)
  const toast = useToast()
  const fileRef = useRef<HTMLInputElement>(null)

  const [file, setFile] = useState<File | null>(null)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [docType, setDocType] = useState('')
  const [contract, setContract] = useState('')
  const [visible, setVisible] = useState(false)
  const [dragOver, setDragOver] = useState(false)

  const handleFile = (f: File) => {
    setFile(f)
    if (!name) setName(f.name.replace(/\.[^.]+$/, ''))
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const f = e.dataTransfer.files[0]
    if (f) handleFile(f)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!file) return

    const fd = new FormData()
    fd.append('file', file)
    fd.append('name', name || file.name)
    fd.append('description', description)
    if (docType) fd.append('document_type', docType)
    if (contract) fd.append('contract', contract)
    if (folderId) fd.append('folder', folderId)
    fd.append('visible_to_employee', visible ? 'true' : 'false')

    try {
      await uploadMutation.mutateAsync(fd)
      toast.success('Documento subido correctamente.')
      onClose()
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Ocurrió un error inesperado.'))
    }
  }

  return (
    <Modal open onClose={onClose} title="Subir documento" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Drag & drop zone */}
        <div
          className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
            dragOver ? 'border-primary bg-primary-50' : 'border-divider hover:border-primary-200'
          }`}
          onDragOver={e => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileRef.current?.click()}
        >
          <input
            ref={fileRef}
            type="file"
            className="hidden"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp"
            onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f) }}
          />
          {file ? (
            <div className="flex items-center justify-center gap-2">
              <FileText size={16} className="text-primary" aria-hidden="true" />
              <span className="text-sm text-content font-medium">{file.name}</span>
              <span className="text-xs text-content-subtle">({formatFileSize(file.size)})</span>
            </div>
          ) : (
            <>
              <Upload size={24} className="mx-auto mb-2 text-content-subtle" aria-hidden="true" />
              <p className="text-sm text-content-muted">Arrastra un archivo o haz clic para seleccionar</p>
              <p className="text-xs text-content-subtle mt-1">PDF, Word, Excel, imágenes — máx. 20 MB</p>
            </>
          )}
        </div>

        {/* Nombre */}
        <div>
          <label className="block text-xs font-medium text-content-muted mb-1" htmlFor="doc-name">
            Nombre del documento
          </label>
          <input
            id="doc-name"
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Ej: Contrato de Trabajo enero 2026"
            className="w-full px-3 py-2 text-sm bg-input-bg border border-input-border rounded-lg text-content placeholder:text-content-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:border-primary-200"
          />
        </div>

        {/* Contrato relacionado */}
        <div>
          <label className="block text-xs font-medium text-content-muted mb-1" htmlFor="doc-contract">
            Contrato relacionado (opcional)
          </label>
          <select
            id="doc-contract"
            value={contract}
            onChange={e => setContract(e.target.value)}
            disabled={contracts.length === 0}
            className="w-full px-3 py-2 text-sm bg-input-bg border border-input-border rounded-lg text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <option value="">— Sin contrato específico —</option>
            {contracts.map(c => (
              <option key={c.id} value={c.id}>
                {c.contract_type} desde {c.start_date}{c.end_date ? ` hasta ${c.end_date}` : ''}
              </option>
            ))}
          </select>
          <p className="text-xs text-content-subtle mt-1">
            {contracts.length === 0
              ? 'Este empleado aún no tiene contratos. Crea uno en el tab Contratos para poder vincular.'
              : 'Útil para vincular contratos, anexos y liquidaciones a un período laboral específico.'}
          </p>
        </div>

        {/* Tipo de documento */}
        <div>
          <label className="block text-xs font-medium text-content-muted mb-1" htmlFor="doc-type">
            Tipo de documento
          </label>
          <select
            id="doc-type"
            value={docType}
            onChange={e => setDocType(e.target.value)}
            className="w-full px-3 py-2 text-sm bg-input-bg border border-input-border rounded-lg text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring"
          >
            <option value="">Sin tipo específico</option>
            {docTypes?.map(dt => (
              <option key={dt.id} value={dt.id}>{dt.name}</option>
            ))}
          </select>
        </div>

        {/* Descripción */}
        <div>
          <label className="block text-xs font-medium text-content-muted mb-1" htmlFor="doc-desc">
            Descripción (opcional)
          </label>
          <textarea
            id="doc-desc"
            value={description}
            onChange={e => setDescription(e.target.value)}
            rows={2}
            placeholder="Notas adicionales sobre el documento..."
            className="w-full px-3 py-2 text-sm bg-input-bg border border-input-border rounded-lg text-content placeholder:text-content-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring resize-none"
          />
        </div>

        {/* Visibilidad al empleado */}
        <label className="flex items-center gap-3 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={visible}
            onChange={e => setVisible(e.target.checked)}
            className="w-4 h-4 rounded accent-primary"
          />
          <span className="text-sm text-content">Visible para el empleado en su portal</span>
        </label>

        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 text-sm font-medium text-content-muted border border-divider rounded-lg hover:bg-surface-muted"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!file || uploadMutation.isPending}
            className="flex-1 px-4 py-2 text-sm font-semibold text-on-primary bg-primary rounded-lg hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploadMutation.isPending ? 'Subiendo...' : 'Subir'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

// ─── New Folder Modal ─────────────────────────────────────────────────────────

interface NewFolderModalProps {
  employeeId: string
  parentId: string | null
  onClose: () => void
}

function NewFolderModal({ employeeId, parentId, onClose }: NewFolderModalProps) {
  const createMutation = useCreateEmployeeFolder(employeeId)
  const toast = useToast()
  const [name, setName] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    try {
      await createMutation.mutateAsync({ name: name.trim(), parent: parentId })
      toast.success('Carpeta creada.')
      onClose()
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Ocurrió un error inesperado.'))
    }
  }

  return (
    <Modal open onClose={onClose} title="Nueva carpeta" size="sm">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-content-muted mb-1" htmlFor="folder-name">
            Nombre de la carpeta
          </label>
          <input
            id="folder-name"
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Ej: Licencias médicas"
            autoFocus
            className="w-full px-3 py-2 text-sm bg-input-bg border border-input-border rounded-lg text-content placeholder:text-content-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:border-primary-200"
          />
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={onClose}
            className="flex-1 px-4 py-2 text-sm font-medium text-content-muted border border-divider rounded-lg hover:bg-surface-muted">
            Cancelar
          </button>
          <button type="submit" disabled={!name.trim() || createMutation.isPending}
            className="flex-1 px-4 py-2 text-sm font-semibold text-on-primary bg-primary rounded-lg hover:bg-primary-hover disabled:opacity-50">
            {createMutation.isPending ? 'Creando...' : 'Crear'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

// ─── Folder Tree Node ─────────────────────────────────────────────────────────

interface FolderNodeProps {
  folder: EmployeeFolder
  selected: string | null
  onSelect: (id: string | null) => void
  depth?: number
}

function FolderNode({ folder, selected, onSelect, depth = 0 }: FolderNodeProps) {
  const [expanded, setExpanded] = useState(true)
  const isSelected = selected === folder.id
  const hasChildren = folder.children.length > 0

  return (
    <div>
      <button
        onClick={() => onSelect(isSelected ? null : folder.id)}
        className={`w-full flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-left text-sm transition-colors ${
          isSelected
            ? 'bg-selected text-primary font-medium'
            : 'text-content hover:bg-hover'
        }`}
        style={{ paddingLeft: `${8 + depth * 16}px` }}
      >
        {hasChildren && (
          <button
            onClick={e => { e.stopPropagation(); setExpanded(prev => !prev) }}
            className="p-0.5 hover:bg-surface-muted rounded"
            aria-label={expanded ? 'Contraer carpeta' : 'Expandir carpeta'}
          >
            <ChevronRight size={12} className={`transition-transform ${expanded ? 'rotate-90' : ''}`} aria-hidden="true" />
          </button>
        )}
        {!hasChildren && <span className="w-[16px]" />}
        {isSelected
          ? <FolderOpen size={14} className="text-primary shrink-0" aria-hidden="true" />
          : <Folder size={14} className="text-content-subtle shrink-0" aria-hidden="true" />
        }
        <span className="truncate flex-1">{folder.name}</span>
        {folder.document_count > 0 && (
          <span className="text-xs text-content-subtle shrink-0">{folder.document_count}</span>
        )}
      </button>

      {expanded && hasChildren && (
        <div>
          {folder.children.map(child => (
            <FolderNode
              key={child.id}
              folder={child}
              selected={selected}
              onSelect={onSelect}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Document Row ─────────────────────────────────────────────────────────────

interface DocRowProps {
  doc: EmployeeDocument
  employeeId: string
  onRefetch: () => void
}

/**
 * Las columnas de los documentos del empleado.
 *
 * 🔴 Era `DocRow`, el último de los cuatro «componente-fila con hook» del
 * inventario. Sus hooks —dos mutaciones, el toast y el estado del modal de
 * mover— sirven a los **seis botones de la última celda**; las cuatro columnas
 * de datos no tocan ninguno.
 *
 * ⚠️ Los anchos fijos se conservan (`width`): sin ellos la tabla se repartía a
 * ojo y «30-07-2026» se cortaba en dos renglones, que era lo que hacía ver las
 * filas de distinto alto. Venía anotado en el `<thead>` que esto reemplaza.
 */
function columnasDeDocumento({ employeeId, onRefetch }: {
  employeeId: string
  onRefetch: () => void
}): DataTableColumn<EmployeeDocument>[] {
  return [
    { key: 'name', header: 'Documento',
      render: doc => (
        <div className="flex items-center gap-2">
          <FileText size={14} className="text-content-subtle shrink-0" aria-hidden="true" />
          {/* `min-w-0` + `break-words`: un nombre largo sin espacios no puede
              ensanchar la celda ni desbordarla. */}
          <div className="min-w-0">
            <NombreDelDocumento doc={doc} />
            <div className="flex items-center gap-2 flex-wrap">
              {doc.document_type_name && (
                <p className="text-xs text-content-subtle">{doc.document_type_name}</p>
              )}
              {doc.contract_label && (
                <span
                  className="text-xs px-1.5 py-0.5 bg-primary-50 text-primary rounded font-medium"
                  title={`Vinculado al contrato: ${doc.contract_label}`}
                >
                  {doc.contract_label}
                </span>
              )}
            </div>
          </div>
        </div>
      ) },
    { key: 'file_size', header: 'Tamaño', width: 80,
      render: doc => (
        <span className="text-xs text-content-subtle whitespace-nowrap">
          {formatFileSize(doc.file_size)}
        </span>
      ) },
    { key: 'uploaded_at', header: 'Subido', width: 112,
      render: doc => (
        <span className="text-xs text-content-subtle whitespace-nowrap">
          {new Date(doc.uploaded_at).toLocaleDateString('es-CL')}
        </span>
      ) },
    { key: 'status', header: 'Estado', width: 96,
      render: doc => (
        <span className={`inline-flex items-center px-1.5 py-0.5 text-xs font-semibold rounded-full ${
          doc.status === 'active'
            ? 'bg-success/10 text-success'
            : doc.status === 'archived'
            ? 'bg-warning/10 text-warning'
            : 'bg-danger/10 text-danger'
        }`}>
          {doc.status === 'active' ? 'Activo' : doc.status === 'archived' ? 'Archivado' : 'Eliminado'}
        </span>
      ) },
    { key: 'acciones', header: 'Acciones', width: 176,
      render: doc => (
        <AccionesDeDocumento doc={doc} employeeId={employeeId} onRefetch={onRefetch} />
      ) },
  ]
}

/**
 * El nombre del documento. Si es un PDF, abre el visor (desde donde también se
 * descarga). Va en el nombre y no como un botón más: el ojo de la celda de
 * acciones ya significa «visible para el trabajador», y otro ojo al lado
 * confundía las dos cosas.
 */
function NombreDelDocumento({ doc }: { doc: EmployeeDocument }) {
  const { ver, visor } = useVisorPdf()
  if ((doc.mime_type || '').toLowerCase() !== 'application/pdf') {
    return <p className="text-sm font-medium text-content break-words">{doc.name}</p>
  }
  return (
    <>
      <button
        type="button"
        onClick={() => ver({
          titulo: doc.name,
          nombreArchivo: doc.name.toLowerCase().endsWith('.pdf') ? doc.name : `${doc.name}.pdf`,
          ...documentoGuardado(apiClient, `/hr-documents/documents/${doc.id}/download/`),
        })}
        title="Ver el PDF"
        className="text-left text-sm font-medium text-primary hover:underline break-words
                   focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring rounded"
      >
        {doc.name}
      </button>
      {visor}
    </>
  )
}

/** La celda de acciones: los seis botones, con sus mutaciones y su modal. */
function AccionesDeDocumento({ doc, employeeId, onRefetch }: DocRowProps) {
  const updateMutation = useUpdateDocument(employeeId)
  const deleteMutation = useDeleteDocument(employeeId)
  const toast = useToast()
  const [showMove, setShowMove] = useState(false)

  const handleArchive = async () => {
    try {
      await updateMutation.mutateAsync({ id: doc.id, status: 'archived' })
      toast.success('Documento archivado.')
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Ocurrió un error inesperado.'))
    }
  }

  const handleToggleVisible = async () => {
    try {
      await updateMutation.mutateAsync({ id: doc.id, visible_to_employee: !doc.visible_to_employee })
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Ocurrió un error inesperado.'))
    }
  }

  const handleDelete = async () => {
    if (!confirm(`¿Eliminar "${doc.name}"? Esta acción es irreversible.`)) return
    try {
      await deleteMutation.mutateAsync(doc.id)
      toast.success('Documento eliminado.')
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Ocurrió un error inesperado.'))
    }
  }

  const handleDownload = async () => {
    try {
      const res = await apiClient.get<{ url: string }>(`/hr-documents/documents/${doc.id}/download/`)
      const signedUrl = res.data?.url
      if (!signedUrl) {
        toast.error('No se pudo obtener el enlace de descarga.')
        return
      }
      window.open(signedUrl, '_blank', 'noopener,noreferrer')
    } catch (err) {
      toast.error(extractErrorMessage(err, 'No se pudo descargar el documento.'))
    }
  }

  return (
    <>
        <div className="flex items-center gap-1 justify-end">
          {/* Toggle visibilidad */}
          <button
            onClick={handleToggleVisible}
            disabled={updateMutation.isPending}
            title={doc.visible_to_employee ? 'Ocultar del portal del empleado' : 'Mostrar al empleado'}
            className={`p-1.5 rounded-lg transition-colors ${
              doc.visible_to_employee
                ? 'text-primary hover:bg-primary-50'
                : 'text-content-subtle hover:bg-surface-muted'
            }`}
            aria-label={doc.visible_to_employee ? 'Ocultar del portal del empleado' : 'Mostrar al empleado'}
          >
            {doc.visible_to_employee
              ? <Eye size={13} aria-hidden="true" />
              : <EyeOff size={13} aria-hidden="true" />
            }
          </button>

          {/* Descargar */}
          <button
            onClick={handleDownload}
            title="Descargar"
            className="p-1.5 rounded-lg text-content-subtle hover:bg-surface-muted hover:text-content"
            aria-label="Descargar documento"
          >
            <Download size={13} aria-hidden="true" />
          </button>

          {/* Mover a otra carpeta */}
          <button
            onClick={() => setShowMove(true)}
            title="Mover a otra carpeta"
            className="p-1.5 rounded-lg text-content-subtle hover:bg-surface-muted hover:text-content"
            aria-label="Mover documento"
          >
            <Move size={13} aria-hidden="true" />
          </button>

          {/* Archivar */}
          {doc.status === 'active' && (
            <button
              onClick={handleArchive}
              disabled={updateMutation.isPending}
              title="Archivar"
              className="p-1.5 rounded-lg text-content-subtle hover:bg-warning/10 hover:text-warning"
              aria-label="Archivar documento"
            >
              <Archive size={13} aria-hidden="true" />
            </button>
          )}

          {/* Eliminar (soft-delete) */}
          <button
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            title="Eliminar"
            className="p-1.5 rounded-lg text-content-subtle hover:bg-danger/10 hover:text-danger"
            aria-label="Eliminar documento"
          >
            <Trash2 size={13} aria-hidden="true" />
          </button>
        </div>
      {showMove && createPortal(
        <MoveDocumentModal
          doc={doc}
          employeeId={employeeId}
          onClose={() => setShowMove(false)}
          onMoved={() => { setShowMove(false); onRefetch() }}
        />,
        document.body,
      )}
    </>
  )
}


function MoveDocumentModal({
  doc, employeeId, onClose, onMoved,
}: {
  doc: EmployeeDocument
  employeeId: string
  onClose: () => void
  onMoved: () => void
}) {
  const { data: folders } = useEmployeeFolders(employeeId)
  const { data: contractsData } = useContracts({ employee_id: employeeId, page_size: 99 })
  const contracts = (contractsData?.contracts ?? contractsData?.results ?? []) as Array<{
    id: string; contract_type: string; start_date: string; end_date: string | null
  }>
  const updateMutation = useUpdateDocument(employeeId)
  const toast = useToast()
  const [folderTarget, setFolderTarget] = useState<string | null>(doc.folder)
  const [contractTarget, setContractTarget] = useState<string | null>(doc.contract)

  const flattenFolders = (list: EmployeeFolder[], prefix = ''): { id: string; label: string }[] => {
    const out: { id: string; label: string }[] = []
    for (const f of list) {
      out.push({ id: f.id, label: prefix + f.name })
      if (f.children?.length) out.push(...flattenFolders(f.children, prefix + f.name + ' / '))
    }
    return out
  }

  const folderOptions = flattenFolders(folders ?? [])

  const folderChanged = folderTarget !== doc.folder
  const contractChanged = contractTarget !== doc.contract
  const hasChanges = folderChanged || contractChanged

  const handleSave = async () => {
    if (!hasChanges) {
      onClose()
      return
    }
    try {
      const patch: { folder?: string | null; contract?: string | null } = {}
      if (folderChanged) patch.folder = folderTarget
      if (contractChanged) patch.contract = contractTarget
      await updateMutation.mutateAsync({ id: doc.id, ...patch })
      toast.success('Documento actualizado.')
      onMoved()
    } catch (err) {
      toast.error(extractErrorMessage(err, 'No se pudo actualizar el documento.'))
    }
  }

  return (
    <Modal open onClose={onClose} title="Editar ubicación y vínculos" size="sm">
      <div className="space-y-3">
        <p className="text-xs text-content-muted truncate">
          <span className="font-medium text-content">{doc.name}</span>
        </p>
        <div>
          <label htmlFor="employeedocumentstab-carpeta" className="block text-xs font-semibold text-content-subtle uppercase tracking-wider mb-1">
            Carpeta
          </label>
          <select id="employeedocumentstab-carpeta"
            value={folderTarget ?? ''}
            onChange={e => setFolderTarget(e.target.value || null)}
            className="w-full text-sm border border-divider rounded-lg px-3 py-2 bg-surface text-content focus:outline-none focus:ring-2 focus:ring-primary/30"
          >
            <option value="">— Sin carpeta (raíz) —</option>
            {folderOptions.map(o => (
              <option key={o.id} value={o.id}>{o.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="employeedocumentstab-contrato-relacionado" className="block text-xs font-semibold text-content-subtle uppercase tracking-wider mb-1">
            Contrato relacionado
          </label>
          <select id="employeedocumentstab-contrato-relacionado"
            value={contractTarget ?? ''}
            onChange={e => setContractTarget(e.target.value || null)}
            disabled={contracts.length === 0}
            className="w-full text-sm border border-divider rounded-lg px-3 py-2 bg-surface text-content focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <option value="">— Sin contrato específico —</option>
            {contracts.map(c => (
              <option key={c.id} value={c.id}>
                {c.contract_type} desde {c.start_date}{c.end_date ? ` hasta ${c.end_date}` : ''}
              </option>
            ))}
          </select>
          {contracts.length === 0 && (
            <p className="text-xs text-content-subtle mt-1">
              Este empleado aún no tiene contratos.
            </p>
          )}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-xs font-semibold text-content-muted hover:bg-surface-muted rounded-lg"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={updateMutation.isPending || !hasChanges}
            className="px-3 py-1.5 text-xs font-semibold text-on-primary bg-primary hover:bg-primary-hover rounded-lg disabled:opacity-50"
          >
            {updateMutation.isPending ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function EmployeeDocumentsTab({ employeeId }: Props) {
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [showUpload, setShowUpload] = useState(false)
  const [showNewFolder, setShowNewFolder] = useState(false)
  const [statusFilter, setStatusFilter] = useState<'active' | 'archived'>('active')

  const certificado = useEmitirCertificadoAntiguedad(employeeId)
  const toastCert = useToast()
  const { data: folders, isLoading: foldersLoading } = useEmployeeFolders(employeeId)
  const { data: docsData, isLoading: docsLoading, refetch } = useEmployeeDocuments({
    employeeId,
    folder: selectedFolder,
    status: statusFilter,
    search: search || undefined,
  })

  const documents = docsData?.documents ?? []

  return (
    /* `flex-1` para cuando el contenedor es flex-col (el modal de empleado) y
       `h-full` para cuando es un bloque con altura propia: con las dos, el tab
       recibe altura acotada en ambos montajes y la tabla puede scrollear. */
    <div className="flex gap-4 flex-1 h-full min-h-0">
      {/* ── Panel izquierdo: árbol de carpetas ── */}
      {/* Scroll propio: con la estructura completa (8 carpetas + anidadas) la
          lista pasa el alto disponible. */}
      <div className="w-52 shrink-0 border-r border-divider pr-3 space-y-1 overflow-y-auto min-h-0">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-bold text-content-subtle uppercase tracking-wider">Carpetas</span>
          <button
            onClick={() => setShowNewFolder(true)}
            title="Nueva carpeta"
            className="p-1 rounded hover:bg-surface-muted text-content-subtle hover:text-content"
            aria-label="Crear nueva carpeta"
          >
            <FolderPlus size={13} aria-hidden="true" />
          </button>
        </div>

        {/* Raíz (todos los documentos) */}
        <button
          onClick={() => setSelectedFolder(null)}
          className={`w-full flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-left text-sm transition-colors ${
            selectedFolder === null
              ? 'bg-selected text-primary font-medium'
              : 'text-content hover:bg-hover'
          }`}
        >
          <Folder size={14} aria-hidden="true" />
          <span>Todos los archivos</span>
        </button>

        {foldersLoading ? (
          <div className="py-4 text-xs text-content-subtle text-center">Cargando...</div>
        ) : (
          (folders ?? []).map(folder => (
            <FolderNode
              key={folder.id}
              folder={folder}
              selected={selectedFolder}
              onSelect={setSelectedFolder}
            />
          ))
        )}
      </div>

      {/* ── Panel derecho: lista de documentos ── */}
      {/* `min-h-0` en la columna: es el eslabón que le falta a la cadena flex
          para que el `overflow-auto` de la tabla (más abajo) tenga efecto. */}
      <div className="flex-1 min-w-0 min-h-0 flex flex-col gap-3">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Búsqueda */}
          <div className="relative flex-1 min-w-[160px]">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-content-subtle" aria-hidden="true" />
            <input aria-label="Buscar documentos"
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar documentos..."
              className="w-full pl-8 pr-3 py-1.5 text-xs bg-input-bg border border-input-border rounded-lg text-content placeholder:text-content-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring"
            />
          </div>

          {/* Filtro estado */}
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value as 'active' | 'archived')}
            className="px-2 py-1.5 text-xs bg-input-bg border border-input-border rounded-lg text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring"
            aria-label="Filtrar por estado"
          >
            <option value="active">Activos</option>
            <option value="archived">Archivados</option>
          </select>

          {/* Certificado de antigüedad (#1491): lo genera Clobi con los datos de
              la ficha, verificable por QR. Queda en la carpeta «Certificados». */}
          <button
            onClick={() => certificado.mutate(undefined, {
              onSuccess: () => toastCert.success('Certificado emitido en la carpeta Certificados.'),
              onError: (e: unknown) => toastCert.error(extractErrorMessage(e, 'No se pudo emitir el certificado.')),
            })}
            disabled={certificado.isPending}
            title="Generar el certificado de antigüedad con los datos de la ficha"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-content border border-divider rounded-lg hover:bg-surface-muted disabled:opacity-50"
          >
            <FileBadge size={12} aria-hidden="true" />
            {certificado.isPending ? 'Generando...' : 'Certificado de antigüedad'}
          </button>

          {/* Subir documento */}
          <button
            onClick={() => setShowUpload(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-on-primary bg-primary rounded-lg hover:bg-primary-hover"
          >
            <Upload size={12} aria-hidden="true" /> Subir
          </button>
        </div>

        {/* Tabla de documentos */}
        <div className="flex-1 overflow-auto rounded-lg border border-divider">
          {docsLoading ? (
            <TableSkeleton rows={5} cols={5} />
          ) : documents.length === 0 ? (
            <div className="p-8">
              <EmptyState
                icon={FileText}
                title="Sin documentos"
                hint={selectedFolder
                  ? 'Esta carpeta no tiene documentos. Sube uno con el botón "Subir".'
                  : 'No hay documentos aún. Sube el primer documento del empleado.'}
                action={
                  <button
                    onClick={() => setShowUpload(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-on-primary bg-primary rounded-lg hover:bg-primary-hover"
                  >
                    <Upload size={12} aria-hidden="true" /> Subir documento
                  </button>
                }
                size="sm"
              />
            </div>
          ) : (
            <DataTable
              columns={columnasDeDocumento({
                employeeId,
                onRefetch: () => refetch(),
              })}
              rows={documents}
              rowKey={doc => doc.id}
              minWidth={720}
              surface={false}
            />
          )}
        </div>
      </div>

      {/* Modales */}
      {showUpload && (
        <UploadModal
          employeeId={employeeId}
          folderId={selectedFolder}
          onClose={() => setShowUpload(false)}
        />
      )}
      {showNewFolder && (
        <NewFolderModal
          employeeId={employeeId}
          parentId={selectedFolder}
          onClose={() => setShowNewFolder(false)}
        />
      )}
    </div>
  )
}
